import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    ExpandedState,
    OnChangeFn,
    RowSelectionState,
} from "@tanstack/react-table";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import {
    ScheduleApplicationsView,
    ScheduleApplicationsViewColumn,
    ScheduleApplicationsViewSchema,
} from "@common/types/views/scheduleApplicationsView";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { useEntitiesById } from "@hooks/useEntities";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { createBillsUrl } from "@components/bills/lib/billsUrl";
import { createInvoicesUrl } from "@components/invoices/lib/invoicesUrl";
import { createTransactionsUrl } from "@components/transactions/lib/transactionsUrl";
import { SCHEDULES_REVIEW_VIEW_ID } from "@components/transactions/views/SchedulesReviewView";
import {
    useScheduleHubCopy,
    useScheduleHubTemplates,
} from "../ScheduleHubScopeProvider";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { collectScheduleTemplateGroups } from "../scheduleListHelpers";
import { usePortalContainer } from "@design-system/overlay/OverlayContainerContext";
import { mergeCachedView } from "@lib/mergeCachedView";
import { useOverlays } from "contexts/OverlayContext";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { ScheduleApplicationsFiltersControls } from "./ScheduleApplicationsFiltersControls";
import { ScheduleLifecycleRequest } from "./ScheduleLifecycleConfirmModal";
import {
    MilestoneAchievedModal,
    MilestoneAchievedTarget,
} from "./MilestoneAchievedModal";
import {
    canSelectScheduleApplicationRow,
    isScheduleApplicationRow,
} from "./milestoneSelectionHelpers";
import { useScheduleLifecycleActions } from "./useScheduleLifecycleActions";
import { useScheduleBillingActions } from "./useScheduleBillingActions";
import {
    ScheduleApplicationParentRow,
    ScheduleApplicationTemplateOption,
    ScheduleApplicationsFilters,
    isScheduleApplicationDrillDownRow,
} from "./scheduleApplicationRowHelpers";
import { getScheduleRowApplicationId } from "./scheduleApplicationRowId";
import { ScheduleApplicationsTableCallbacks } from "./scheduleApplicationsTableMeta";
import { scheduleApplicationsViewConfig } from "./scheduleApplicationsViewConfig";
import { buildSelectedSchedulesReportUrl } from "./scheduleApplicationsReportLink";
import { SchedulesReportLayout } from "@common/types/reports/schedules-report";
import styles from "./ScheduleApplicationsDrawer.module.scss";

const SHARED_DEFAULT_COLUMNS: ScheduleApplicationsView["columns"] = [
    { id: ScheduleApplicationsViewColumn.TYPE },
    { id: ScheduleApplicationsViewColumn.REFERENCE },
    { id: ScheduleApplicationsViewColumn.DESCRIPTION },
    { id: ScheduleApplicationsViewColumn.LINE_ITEM },
    { id: ScheduleApplicationsViewColumn.ENTITY },
    { id: ScheduleApplicationsViewColumn.COUNTERPARTY },
    { id: ScheduleApplicationsViewColumn.CLASS },
    { id: ScheduleApplicationsViewColumn.SPLIT },
    { id: ScheduleApplicationsViewColumn.AMOUNT },
    { id: ScheduleApplicationsViewColumn.REMAINING_BALANCE },
    { id: ScheduleApplicationsViewColumn.MATCH },
    { id: ScheduleApplicationsViewColumn.STATUS },
    {
        id: ScheduleApplicationsViewColumn.ACTIONS,
        options: { pin: "right" },
    },
];

function withMatchColumn(
    view: ScheduleApplicationsView,
): ScheduleApplicationsView {
    if (
        view.columns.some(
            (column) => column.id === ScheduleApplicationsViewColumn.MATCH,
        )
    ) {
        return view;
    }
    const remainingIndex = view.columns.findIndex(
        (column) =>
            column.id === ScheduleApplicationsViewColumn.REMAINING_BALANCE,
    );
    const matchColumn = { id: ScheduleApplicationsViewColumn.MATCH };
    if (remainingIndex < 0) {
        return { ...view, columns: [...view.columns, matchColumn] };
    }
    const columns = [...view.columns];
    columns.splice(remainingIndex + 1, 0, matchColumn);
    return { ...view, columns };
}

const SHARED_DEFAULT_OPTIONS: ScheduleApplicationsView["options"] = {
    selection: false,
    bulkActions: false,
    density: ViewDensity.LG,
};

export const SCHEDULE_APPLICATIONS_PAGE_DEFAULT_VIEW: ScheduleApplicationsView =
    {
        type: ViewType.SCHEDULE_APPLICATIONS,
        columns: [
            { id: ScheduleApplicationsViewColumn.DATE },
            { id: ScheduleApplicationsViewColumn.TEMPLATE },
            ...SHARED_DEFAULT_COLUMNS,
        ],
        filters: {},
        options: SHARED_DEFAULT_OPTIONS,
    };

interface ScheduleApplicationsTableProps {
    /** Already narrowed to the current filters, search and page. */
    rows: ScheduleApplicationParentRow[];
    isLoading: boolean;
    viewCacheKey: WORKSPACE_USER_CACHE_KEYS;
    defaultView: ScheduleApplicationsView;
    filters: ScheduleApplicationsFilters;
    onFiltersChange: (filters: ScheduleApplicationsFilters) => void;
    search: string;
    onSearchChange: (search: string) => void;
    /** Counterparty filter options; independent of the rows on screen. */
    counterpartyNames: string[];
    lineItemLabels: string[];
    /** Policy filter options; omitted when the list is already scoped to one policy. */
    templateOptions?: ScheduleApplicationTemplateOption[];
    /** When set, opens this schedule's journal lines. */
    applicationId?: string | null;
    tabNavigationTop?: ReactNode;
    footer?: ReactNode;
    /** When set, schedule rows get checkboxes; of their ladder rows, only pending milestones do. */
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    /** Bound the table to the viewport so it scrolls itself and the footer stays pinned. */
    isFullPage?: boolean;
    onOpenReport: (url: string) => void;
}

export const ScheduleApplicationsTable = ({
    rows,
    isLoading,
    viewCacheKey,
    defaultView,
    filters,
    onFiltersChange,
    search,
    onSearchChange,
    counterpartyNames,
    lineItemLabels,
    templateOptions,
    applicationId = null,
    tabNavigationTop,
    footer,
    rowSelection,
    onRowSelectionChange,
    isFullPage = false,
    onOpenReport,
}: ScheduleApplicationsTableProps) => {
    const navigate = useStateAwareNavigate();
    const { data: templates } = useScheduleHubTemplates({
        includeArchived: true,
        includeMilestone: true,
    });
    const { schedulesEmptyStateHeader } = useScheduleHubCopy();
    const groupNames = useMemo(
        () => collectScheduleTemplateGroups(templates ?? []),
        [templates],
    );
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const entitiesById = useEntitiesById({ onlyBusiness: true });
    const portalContainer = usePortalContainer();

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<ScheduleApplicationsView>(
            viewCacheKey,
            ScheduleApplicationsViewSchema,
        );
    const [view, setView] = useState<ScheduleApplicationsView>(() =>
        withMatchColumn(
            mergeCachedView(
                cachedView,
                defaultView,
                scheduleApplicationsViewConfig,
            ),
        ),
    );
    const expandedRowIdForApplication = useCallback(
        (scheduleApplicationId: string) =>
            rows.some((row) => row.id === scheduleApplicationId)
                ? scheduleApplicationId
                : (rows.find(
                      (row) =>
                          getScheduleRowApplicationId(row) ===
                          scheduleApplicationId,
                  )?.id ?? scheduleApplicationId),
        [rows],
    );
    const deepLinkedRowId = applicationId
        ? expandedRowIdForApplication(applicationId)
        : null;
    const [expanded, setExpanded] = useState<ExpandedState>(() =>
        deepLinkedRowId ? { [deepLinkedRowId]: true } : {},
    );
    useEffect(() => {
        if (!deepLinkedRowId) {
            return;
        }
        setExpanded((current) =>
            current === true
                ? current
                : { ...current, [deepLinkedRowId]: true },
        );
    }, [deepLinkedRowId]);
    const isSelectable = rowSelection !== undefined;
    const tableView = useMemo(
        () =>
            isSelectable
                ? {
                      ...view,
                      options: {
                          ...view.options,
                          bulkActions: true as const,
                          selection: true as const,
                      },
                  }
                : view,
        [isSelectable, view],
    );
    const tableRef = useRef<ViewTableHandle>(null);
    const handleLifecycleCompleted = useCallback(
        (request: ScheduleLifecycleRequest) => {
            const expandedRowId = expandedRowIdForApplication(
                request.applicationId,
            );
            setExpanded((current) =>
                current === true
                    ? current
                    : { ...current, [expandedRowId]: true },
            );
        },
        [expandedRowIdForApplication],
    );
    const { requestLifecycleAction, modals: lifecycleModals } =
        useScheduleLifecycleActions({ onCompleted: handleLifecycleCompleted });
    const { schedulesMilestoneBased: isMilestoneEnabled } = useTypedFlags();
    const [milestoneTarget, setMilestoneTarget] =
        useState<MilestoneAchievedTarget | null>(null);
    const { requestBillingAction, modals: billingModals } =
        useScheduleBillingActions();

    const handleViewChange = useCallback(
        (newView: ScheduleApplicationsView) => {
            setView(newView);
            setCachedView(newView);
        },
        [setCachedView],
    );

    const handleViewChangeFromTable = useCallback(
        (newView: View) => {
            const parsed = ScheduleApplicationsViewSchema.safeParse(newView);
            if (parsed.success) {
                handleViewChange(parsed.data);
            }
        },
        [handleViewChange],
    );

    const tableMeta = useMemo(
        () => ({
            callbacks: {
                onOpenJournalEntry: ({
                    referenceNumber,
                    entityId,
                    ledgerId,
                    ledgerBasis,
                }) => {
                    const entity = entitiesById.get(entityId);
                    if (!entity) {
                        return;
                    }
                    openOverlay("journalEntryDrawer", {
                        entity,
                        journalEntryId: referenceNumber,
                        ledgerId,
                        ledgerBasis,
                    });
                },
                onOpenTransaction: ({ transactionId, entityId }) => {
                    navigate(
                        createTransactionsUrl(
                            { entityIds: [entityId] },
                            {
                                view: SCHEDULES_REVIEW_VIEW_ID,
                                transactionId,
                            },
                        ),
                    );
                },
                onOpenInvoice: ({ invoiceId, groupBy }) => {
                    navigate(createInvoicesUrl({}, { invoiceId, groupBy }));
                },
                onOpenBill: (billId) => {
                    navigate(createBillsUrl({ billIds: [billId] }));
                },
                onRequestLifecycleAction: requestLifecycleAction,
                onMilestoneAchieved: isMilestoneEnabled
                    ? (row) => {
                          if (!row.milestone || refuseIfViewOnly()) {
                              return;
                          }
                          setMilestoneTarget({
                              applicationId: row.scheduleApplicationId,
                              entityId: row.entityId,
                              amount: row.amount,
                              milestone: row.milestone,
                          });
                      }
                    : undefined,
                onRequestBillingAction: requestBillingAction,
                onOpenInReports: (row) => {
                    const url = buildSelectedSchedulesReportUrl({
                        selectedRows: [row],
                        layout: SchedulesReportLayout.waterfall,
                        periodStart: filters.periodStart,
                        periodEnd: filters.periodEnd,
                    });
                    if (url) {
                        onOpenReport(url);
                    }
                },
            } satisfies ScheduleApplicationsTableCallbacks,
            entitiesById,
            getRowSelectionHidden: (row: unknown) =>
                isScheduleApplicationRow(row) &&
                !canSelectScheduleApplicationRow({ row, isMilestoneEnabled }),
        }),
        [
            entitiesById,
            filters.periodEnd,
            filters.periodStart,
            isMilestoneEnabled,
            navigate,
            refuseIfViewOnly,
            onOpenReport,
            openOverlay,
            requestBillingAction,
            requestLifecycleAction,
        ],
    );

    return (
        <>
            <TablePageHeader
                isFullPage
                search={search}
                searchPlaceholder="Search schedules..."
                onSearchChange={onSearchChange}
                borderBottom
                tabNavigationTop={tabNavigationTop}
                afterFiltersChildren={
                    <ScheduleApplicationsFiltersControls
                        filters={filters}
                        onFiltersChange={onFiltersChange}
                        counterpartyNames={counterpartyNames}
                        lineItemLabels={lineItemLabels}
                        templateOptions={templateOptions}
                        groupNames={groupNames}
                        portalContainer={portalContainer}
                    />
                }
            >
                <EntityFilterCombobox
                    filters={{ entityIds: filters.entityIds }}
                    onChange={({ entityIds }) =>
                        onFiltersChange({
                            ...filters,
                            entityIds: entityIds ?? [],
                        })
                    }
                    onlyBusiness
                    container={portalContainer}
                />
                <ColumnSettings
                    view={view}
                    viewConfig={scheduleApplicationsViewConfig}
                    defaultView={defaultView}
                    onViewChange={handleViewChange}
                    portalContainer={portalContainer}
                />
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
            </TablePageHeader>
            {isLoading ? (
                <Loader />
            ) : (
                <ViewTable
                    ref={tableRef}
                    data={rows}
                    view={tableView}
                    footer={footer}
                    rowSelection={rowSelection}
                    onRowSelectionChange={onRowSelectionChange}
                    enableRowSelection={(row) =>
                        canSelectScheduleApplicationRow({
                            row,
                            isMilestoneEnabled,
                        })
                    }
                    enableSubRowSelection={false}
                    fixedTopOffsetPx={isFullPage ? true : undefined}
                    expanded={expanded}
                    onExpandedChange={setExpanded}
                    config={scheduleApplicationsViewConfig}
                    onViewChange={handleViewChangeFromTable}
                    className={styles.scheduleApplicationsTable}
                    emptyView={() => (
                        <EmptyState header={schedulesEmptyStateHeader} />
                    )}
                    loadingView={() => <Loader />}
                    tableMeta={tableMeta}
                    getRowClassName={(row) =>
                        isScheduleApplicationDrillDownRow(row)
                            ? styles.drillDownRow
                            : styles.applicationRow
                    }
                />
            )}
            {lifecycleModals}
            <MilestoneAchievedModal
                target={milestoneTarget}
                onClose={() => setMilestoneTarget(null)}
            />
            {billingModals}
        </>
    );
};
